import { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Avatar,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { ClienteContext } from "../context/ClienteContext.js";
import { CarroContext } from "../context/CarroContext.js";
import { useNavigate } from "react-router-dom";
import actualizarCliente from "../services/actualizacionClientes.js";

const Compra = () => {
  const { cliente, setCliente } = useContext(ClienteContext);
  const { carro } = useContext(CarroContext);
  const navigate = useNavigate();

  const [direccion, setDireccion] = useState(cliente?.direccion || "");
  const [telefono, setTelefono] = useState(cliente?.telefono?.replace("+34", "") || "");

  const [errorDireccion, setErrorDireccion] = useState(false);
  const [errorTelefono, setErrorTelefono] = useState(false);

  const [openSuccess, setOpenSuccess] = useState(false);
  const [openError, setOpenError] = useState(false);

  // Diálogo de advertencia
  const [openWarning, setOpenWarning] = useState(false);

  const total = carro.reduce(
    (acumulado, item) => acumulado + item.producto.precio * item.cantidad,
    0
  );

  const handleActualizarDireccion = async () => {
    const direccionValida = direccion.trim();
    const telefonoValido = telefono.trim();
    const telefonoFormatoValido = /^\d{9}$/.test(telefonoValido);

    if (direccionValida && telefonoFormatoValido) {
      const clienteActualizado = {
        ...cliente,
        direccion: direccionValida,
        telefono: `+34${telefonoValido}`
      };

      const respuestaCorrecta = await actualizarCliente(clienteActualizado);

      if (respuestaCorrecta) {
        setCliente(clienteActualizado);
        sessionStorage.setItem("cliente", JSON.stringify(clienteActualizado));
        setOpenSuccess(true);
      } else {
        setOpenError(true);
      }
    }
  };

  const enviarPagoARedsys = async () => {
    try {
      const respuesta = await fetch(`${process.env.REACT_APP_API_URL}/pago/generarPago`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cliente, carro, total })
      });

      const data = await respuesta.json();

      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.url;

      const campos = {
        Ds_SignatureVersion: data.version,
        Ds_MerchantParameters: data.params,
        Ds_Signature: data.signature
      };

      for (const [key, value] of Object.entries(campos)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error("Error al generar el formulario de pago:", error);
      setOpenError(true);
    }
  };

  const handlePagar = async () => {
    const direccionValida = direccion.trim();
    const telefonoValido = telefono.trim();
    const telefonoFormatoValido = /^\d{9}$/.test(telefonoValido);

    setErrorDireccion(!direccionValida);
    setErrorTelefono(!telefonoFormatoValido);

    if (!direccionValida || !telefonoFormatoValido) return;

    await handleActualizarDireccion();
    await enviarPagoARedsys();
  };

  const confirmarPago = async () => {
    setOpenWarning(false);
    await handlePagar();
  };

  const handleVolver = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!cliente.nombre) {
      navigate('/datosCliente');
    }
  }, []);

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", mt: 4, p: 3 }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Avatar
          src="/images/logo.png"
          alt="Logo"
          sx={{ width: 100, height: 100, mx: "auto" }}
        />
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Datos del cliente
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          label="Nombre del cliente"
          value={cliente?.nombre || ""}
          InputProps={{ readOnly: true }}
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          <TextField
            fullWidth
            variant="outlined"
            label="Dirección de envío"
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            placeholder="Introduce tu dirección de envío"
            error={errorDireccion}
            helperText={errorDireccion ? "Por favor, introduce una dirección" : ""}
            sx={{ flex: 1 }}
          />
          <TextField
            fullWidth
            variant="outlined"
            label="Teléfono (9 dígitos)"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value.replace(/\D/g, ""))}
            placeholder="Ej: 612345678"
            error={errorTelefono}
            helperText={
              errorTelefono
                ? "Introduce un número válido de 9 dígitos"
                : "Se añadirá automáticamente el prefijo +34"
            }
            sx={{ flex: 1 }}
          />
          <Tooltip title="Actualizar datos">
            <IconButton
              color="primary"
              onClick={handleActualizarDireccion}
              sx={{ mt: "4px" }}
            >
              <SaveIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Resumen del pedido
        </Typography>

        {carro.map((item) => (
          <Box key={item.producto.id} sx={{ my: 1 }}>
            <Typography>
              {item.cantidad} × {item.producto.nombre} —{" "}
              {(item.producto.precio * item.cantidad).toFixed(2)} €
            </Typography>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" align="right" sx={{ fontWeight: "bold" }}>
          Total: {total.toFixed(2)} €
        </Typography>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="contained" color="secondary" onClick={handleVolver}>
          Volver
        </Button>

        <Button variant="contained" color="primary" onClick={() => setOpenWarning(true)}>
          Pagar
        </Button>
      </Box>

      {/* DIÁLOGO PREMIUM */}
      <Dialog open={openWarning} onClose={() => setOpenWarning(false)}>
        <DialogTitle sx={{ fontWeight: "bold", color: "red" }}>
          Atención
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Esta es una compra simulada. Para realizar el pago use la siguiente tarjeta de pruebas:
          </Typography>

          {/* TARJETA PREMIUM */}
          <Box
            sx={{
              width: 340,
              height: 210,
              mx: "auto",
              perspective: "1000px",
              mb: 2
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: 4,
                position: "relative",
                transformStyle: "preserve-3d",
                animation: "flipCard 1s ease",
                "@keyframes flipCard": {
                  "0%": { transform: "rotateY(-90deg)" },
                  "100%": { transform: "rotateY(0deg)" }
                },
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.3)",
                boxShadow: "0 8px 25px rgba(0,0,0,0.3)",
                overflow: "hidden",
                p: 2
              }}
            >
              {/* Fondo degradado */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                  opacity: 0.9,
                  zIndex: -1
                }}
              />

              {/* LOGO REAL DE LA TIENDA */}
              <Box
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  width: 60,
                  height: 60,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <img
                  src="logo.png"
                  alt="VCA Shop Logo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    filter: "drop-shadow(0 0 4px rgba(0,0,0,0.4))"
                  }}
                />
              </Box>

              {/* Chip metálico */}
              <Box
                sx={{
                  width: 50,
                  height: 40,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, #d4d4d4, #8c8c8c, #e6e6e6)",
                  mt: 1,
                  mb: 3
                }}
              />

              {/* Número de tarjeta */}
              <Typography
                sx={{
                  fontSize: 22,
                  letterSpacing: 2,
                  color: "white",
                  mb: 2
                }}
              >
                4548 8100 0000 0003
              </Typography>

              {/* Exp y CVV */}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ color: "white", fontSize: 14 }}>
                  <strong>EXP:</strong> 12/49
                </Typography>
                <Typography sx={{ color: "white", fontSize: 14 }}>
                  <strong>CVV:</strong> 123
                </Typography>
              </Box>

              {/* Holograma animado */}
              <Box
                sx={{
                  width: 60,
                  height: 40,
                  borderRadius: "8px",
                  background:
                    "linear-gradient(45deg, #ff9a9e, #fad0c4, #fad0c4, #fbc2eb)",
                  backgroundSize: "300% 300%",
                  animation: "holo 4s ease infinite",
                  position: "absolute",
                  bottom: 10,
                  right: 10,
                  "@keyframes holo": {
                    "0%": { backgroundPosition: "0% 50%" },
                    "50%": { backgroundPosition: "100% 50%" },
                    "100%": { backgroundPosition: "0% 50%" }
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenWarning(false)} color="secondary">
            Cancelar
          </Button>
          <Button onClick={confirmarPago} color="primary" variant="contained">
            Continuar con el pago
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSuccess}
        autoHideDuration={3000}
        onClose={() => setOpenSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setOpenSuccess(false)}>
          Datos actualizados correctamente
        </Alert>
      </Snackbar>

      <Snackbar
        open={openError}
        autoHideDuration={3000}
        onClose={() => setOpenError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setOpenError(false)}>
          No se pudo actualizar los datos. Inténtalo de nuevo.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Compra;